import type { ValidationReport } from '@photovalidator/core';
import { renderMarkers } from './markers.js';
import { buildOverlayPlan, type OverlayPlan } from './overlayPlan.js';
import { clearOverlay, renderOverlay } from './overlayRenderer.js';
import { baseRect, normalizedToScreen, screenToNormalized, type Point, type ScreenRect } from './layout.js';
import { el } from '../ui/dom.js';

/**
 * The photo stage — a **viewer**, not an editor.
 *
 * DOM layout:
 *
 *   .pv-stage                     ← fixed-height area
 *     .pv-stage__frame          ← contain-fit area, no user transform
 *       .pv-stage__content        ← sized to the contain-fit rectangle
 *         canvas.pv-stage__image  ← the user's original photo, unchanged
 *         svg.pv-stage__overlay   ← normalized diagnostic overlay
 *     .pv-stage__markers           ← HTML labels/pins in screen space
 *
 * There is deliberately no zoom, pan, rotation or cropping: the validator
 * answers "does this photo fit the document?", it does not help to "fix" the
 * photo. Tapping highlights the measurement under the finger — diagnostics,
 * not editing.
 */

export interface PhotoStageCallbacks {
  onSelectMeasurement?(measurementId: string): void;
}

export interface PhotoStageOptions {
  /** Long side of the display canvas; keeps memory sane for 40 MP photos. */
  maxDisplaySide?: number;
}

const EMPTY_PLAN: OverlayPlan = {
  shapes: [],
  labels: [],
  problemMarkers: [],
  landmarks: [],
  drawn: 0,
  hidden: 0,
};

export class PhotoStage {
  readonly element: HTMLElement;

  private readonly frameElement: HTMLElement;
  private readonly content: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly svg: SVGSVGElement;
  private readonly markerLayer: HTMLElement;
  private readonly callbacks: PhotoStageCallbacks;
  private readonly maxDisplaySide: number;

  private imageSize = { width: 1, height: 1 };
  private plan: OverlayPlan = EMPTY_PLAN;
  private report: ValidationReport | undefined;
  private showOverlay = true;
  private detailMode = false;
  private highlightId: string | undefined;
  private mode: 'report' | 'live' = 'report';
  private observer: ResizeObserver | null = null;

  constructor(callbacks: PhotoStageCallbacks = {}, options: PhotoStageOptions = {}) {
    this.callbacks = callbacks;
    this.maxDisplaySide = options.maxDisplaySide ?? 3000;

    this.canvas = el('canvas', { class: 'pv-stage__image' });
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('class', 'pv-stage__overlay');
    this.content = el('div', { class: 'pv-stage__content' }, [this.canvas, this.svg]);
    this.frameElement = el('div', { class: 'pv-stage__frame' }, [this.content]);
    this.markerLayer = el('div', { class: 'pv-stage__markers' });
    this.element = el('div', { class: 'pv-stage pv-stage--hide-overlay' }, [
      this.frameElement,
      this.markerLayer,
    ]);

    // Tap = diagnostics: highlight the measurement under the point.
    this.element.addEventListener('click', (event: MouseEvent) => {
      const measurementId = this.hitTest(this.localPoint(event));
      if (measurementId) this.callbacks.onSelectMeasurement?.(measurementId);
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.layout());
      this.observer.observe(this.element);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.layout);
    }
  }

  // ------------------------------------------------------------------ source

  /** Draws the working bitmap into the display canvas, unchanged. */
  setImage(bitmap: ImageBitmap): void {
    this.drawInto(bitmap, bitmap.width, bitmap.height);
  }

  /** Draws a camera frame into the same canvas (live preview). */
  drawSource(source: CanvasImageSource, width: number, height: number): void {
    this.drawInto(source, width, height);
  }

  /** Empties the stage (used by «Проверить другое фото»). */
  clear(): void {
    const ctx = this.canvas.getContext('2d');
    ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvas.width = 1;
    this.canvas.height = 1;
    this.imageSize = { width: 1, height: 1 };
    this.report = undefined;
    this.plan = EMPTY_PLAN;
    this.highlightId = undefined;
    this.refreshOverlay();
    this.layout();
  }

  private drawInto(source: CanvasImageSource, sourceWidth: number, sourceHeight: number): void {
    const scale = Math.min(1, this.maxDisplaySide / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const sizeChanged = width !== this.canvas.width || height !== this.canvas.height;
    if (sizeChanged) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    const ctx = this.canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(source, 0, 0, width, height);
    }
    // The display canvas may be downscaled for memory; geometry stays
    // normalized, so proportions and the overlay are unaffected.
    this.imageSize = { width, height };
    // Camera frames arrive continuously: only re-layout when the size changed.
    if (sizeChanged) this.layout();
  }

  /** Applies every overlay-affecting input in one pass. */
  applyState(next: {
    report?: ValidationReport | undefined;
    showOverlay?: boolean;
    detailMode?: boolean;
    highlightId?: string | undefined;
    mode?: 'report' | 'live';
  }): void {
    if (next.showOverlay !== undefined) {
      this.showOverlay = next.showOverlay;
      this.element.classList.toggle('pv-stage--hide-overlay', !next.showOverlay);
    }
    if (next.detailMode !== undefined) this.detailMode = next.detailMode;
    if (next.mode !== undefined) this.mode = next.mode;
    if (next.report !== undefined && next.report !== this.report) this.report = next.report;
    if (next.highlightId !== this.highlightId) this.highlightId = next.highlightId;
    this.refreshOverlay();
  }

  /** Highlights a measurement without moving anything. */
  focusMeasurement(measurementId: string): void {
    this.highlightId = measurementId;
    this.refreshOverlay();
  }

  destroy(): void {
    this.observer?.disconnect();
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.layout);
  }

  // ----------------------------------------------------------------- private

  /** Base rect of the content element, relative to the marker layer. */
  private base(): ScreenRect {
    return baseRect(
      { width: this.element.clientWidth, height: this.element.clientHeight },
      this.imageSize,
    );
  }

  private localPoint(event: MouseEvent): Point {
    const rect = this.element.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** Nearest measurement whose plan geometry contains the tapped point. */
  private hitTest(point: Point): string | undefined {
    const base = this.base();
    let best: { id: string; distance: number } | undefined;
    for (const shape of this.plan.shapes) {
      if (shape.role === 'guide' && !shape.measurementId.startsWith('guide.head')) continue;
      const values = Object.values(shape.attributes).filter((value) => value <= 1);
      for (let index = 0; index + 1 < values.length; index += 2) {
        const x = values[index];
        const y = values[index + 1];
        if (x === undefined || y === undefined) continue;
        const screen = normalizedToScreen({ x, y }, base);
        const distance = Math.hypot(screen.x - point.x, screen.y - point.y);
        if (distance < 28 && (!best || distance < best.distance)) {
          best = { id: shape.measurementId, distance };
        }
      }
    }
    return best?.id;
  }

  private layout = (): void => {
    const base = this.base();
    this.content.style.width = `${base.width}px`;
    this.content.style.height = `${base.height}px`;
    this.refreshOverlay();
  };

  private refreshOverlay(): void {
    const base = this.base();

    // Overlay switched off = clean photo, no markup, no markers.
    if (!this.showOverlay) {
      clearOverlay(this.svg);
      this.markerLayer.replaceChildren();
      return;
    }

    this.plan = buildOverlayPlan(this.report, {
      baseSize: { width: Math.max(1, base.width), height: Math.max(1, base.height) },
      showGuides: true,
      detailMode: this.detailMode,
      ...(this.highlightId ? { highlightId: this.highlightId } : {}),
      mode: this.mode,
    });

    renderOverlay(this.svg, this.plan, {
      // No user zoom exists, so the stroke width is simply constant.
      strokeWidth: 1.8,
      baseSize: { width: Math.max(1, base.width), height: Math.max(1, base.height) },
    });

    this.content.style.width = `${base.width}px`;
    this.content.style.height = `${base.height}px`;
    this.renderMarkerLayer();
  }

  private renderMarkerLayer(): void {
    renderMarkers(this.markerLayer, {
      plan: this.plan,
      base: this.base(),
      layerSize: { width: this.element.clientWidth, height: this.element.clientHeight },
      onSelect: (measurementId) => this.callbacks.onSelectMeasurement?.(measurementId),
    });
  }
}

/** Exported for tests: the static mapping used by markers and hit tests. */
export const stagePoint = normalizedToScreen;
export const stagePointToNormalized = screenToNormalized;
